import { CircleAlert, CircleCheck, Info, TriangleAlert } from 'lucide-react';
import type { ReactNode } from 'react';

import { cn } from '~/utils/cn';

type Tone = 'warning' | 'error' | 'info' | 'success';

const TONE: Record<Tone, { box: string; icon: string; Icon: typeof Info }> = {
  warning: { box: 'border-warning/40 bg-warning-50', icon: 'text-warning', Icon: TriangleAlert },
  error: { box: 'border-error/40 bg-error-50', icon: 'text-error', Icon: CircleAlert },
  info: { box: 'border-info/30 bg-info-50', icon: 'text-info', Icon: Info },
  success: { box: 'border-border bg-muted', icon: 'text-foreground', Icon: CircleCheck },
};

interface Props {
  tone: Tone;
  title: ReactNode;
  children?: ReactNode;
  /** One action (button/link) aligned right. */
  action?: ReactNode;
  className?: string | undefined;
}

/**
 * A problem or notice stated where it matters (R6): at the top of its card, with why it
 * happened and what it means — never as row 8 of a label/value list.
 */
export function Callout({ tone, title, children, action, className }: Props) {
  const { box, icon, Icon } = TONE[tone];
  return (
    <div
      role={tone === 'error' || tone === 'warning' ? 'alert' : 'status'}
      className={cn('flex items-start gap-3 rounded-lg border px-3 py-2.5', box, className)}
    >
      <Icon aria-hidden="true" className={cn('mt-0.5 size-4 shrink-0', icon)} />
      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        <p className="text-foreground text-sm font-medium">{title}</p>
        {children ? <div className="text-muted-foreground text-sm">{children}</div> : null}
      </div>
      {action ? <div className="shrink-0 self-center">{action}</div> : null}
    </div>
  );
}
